import React, {useEffect, useState, useMemo} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './OptionalInstruction.scss';
import ArrAccordionWithCheckbox from '../../../common/arr-accordion-with-checkbox/ArrAccordionWithCheckbox';
import {useTranslation} from 'react-i18next';
import TranslationComponent from './translation-component/TranslationComponent';
import {
  GetProductionComponentsResponse,
  GetTranslationComponentResponse,
} from '../../../../api/types/production.type';
import Mp3Components from './mp3-components/Mp3Components';
import {FormMode} from '../CreateNewProductionUtils';

// Constants outside component to avoid recreation
const MP3_SECTION_ID = 'MP3 Files Processed for Audio Review';
const TRANSLATIONS_SECTION_ID = 'Translations';

type OptionalSectionState = {
  id: string;
  checked: boolean;
};

type SectionConfig = {
  id: string;
  title: {primaryText: string};
  checked: boolean;
};

interface OptionalInstructionsComponentsProps {
  productionComponentsData: GetProductionComponentsResponse | undefined;
  translationComponentData: GetTranslationComponentResponse | undefined;
  onStateChange: (state: OptionalSectionState[]) => void;
  productionId: number;
  isProductionSet: boolean;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
  isInProgressOrCompleted?: boolean;
}

const OptionalInstruction: React.FC<OptionalInstructionsComponentsProps> = ({
  productionComponentsData,
  translationComponentData,
  onStateChange,
  productionId,
  isProductionSet,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId = 0,
  isInProgressOrCompleted = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {t} = useTranslation();
  const [isMP3FileSelected, setIsMP3FileSelected] = useState<boolean>(false);
  const [isTransalationsSelected, setIsTransalationsSelected] =
    useState<boolean>(false);

  // Store only config data (no JSX) in state
  const [sections, setSections] = useState<SectionConfig[]>([
    {
      id: MP3_SECTION_ID,
      title: {primaryText: 'MP3 Files Processed for Audio Review'},
      checked: false,
    },
    {
      id: TRANSLATIONS_SECTION_ID,
      title: {
        primaryText: 'Translations',
      },
      checked: false,
    },
  ]);

  // Render content based on section ID and current state
  const renderSectionContent = (sectionId: string) => {
    switch (sectionId) {
      case MP3_SECTION_ID:
        return (
          <Mp3Components
            isMP3FileSelected={isMP3FileSelected}
            productionId={productionId}
            isProductionSet={isProductionSet}
            mode={mode}
            isTemplate={isTemplate}
            templateId={templateId}
          />
        );
      case TRANSLATIONS_SECTION_ID:
        return (
          <TranslationComponent
            fileGroupList={productionComponentsData?.fileGroupTypeList || []}
            translationComponentData={translationComponentData}
            isTransalationsSelected={isTransalationsSelected}
            isProductionSet={isProductionSet}
            mode={mode}
            isTemplate={isTemplate}
            templateId={templateId}
          />
        );
      default:
        return <></>;
    }
  };

  const handleCheckChange = (sectionId: string, checked: boolean) => {
    // Update specific state flags directly using the checked parameter
    if (sectionId === MP3_SECTION_ID) {
      setIsMP3FileSelected(checked);
    } else if (sectionId === TRANSLATIONS_SECTION_ID) {
      setIsTransalationsSelected(checked);
    }

    // Update sections state and notify parent
    setSections((prev) => {
      const updatedSections = prev.map((sec) =>
        sec.id === sectionId ? {...sec, checked} : sec
      );

      // Notify parent with updated state
      onStateChange(updatedSections);

      return updatedSections;
    });
  };

  // Initialize from API data
  useEffect(() => {
    if (productionComponentsData?.productionComponentModel) {
      const componentModel = productionComponentsData.productionComponentModel;

      // Update sections state and notify parent
      setSections((prev) => {
        const updatedSections = prev.map((sec) => {
          if (sec.id === TRANSLATIONS_SECTION_ID) {
            return {...sec, checked: componentModel.isTranslation};
          }
          if (sec.id === MP3_SECTION_ID) {
            return {...sec, checked: componentModel.isMP3FileSelected};
          }
          return sec;
        });
        // Notify parent with updated state
        onStateChange(updatedSections);
        return updatedSections;
      });

      setIsMP3FileSelected(componentModel.isMP3FileSelected);
      setIsTransalationsSelected(componentModel.isTranslation);
    }
  }, [productionComponentsData?.productionComponentModel]);

  // Transform sections to include content for ArrAccordionWithCheckbox
  const sectionsWithContent = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        content: renderSectionContent(section.id),
      })),
    [
      sections,
      isMP3FileSelected,
      isTransalationsSelected,
      productionComponentsData?.fileGroupTypeList,
      translationComponentData,
    ]
  );

  return (
    <div
      className="expandable-container"
      data-testid={`optional-instruction-div`}
    >
      <button
        type="button"
        className="expandable-header"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="optional-instruction-expand-button"
      >
        <span
          className="expandable-title"
          data-testid={`optional-instruction-span`}
        >
          {isOpen
            ? t(
                'productionsAndExports.createNewProduction.productionComponents.hideOptionalInstructions'
              )
            : t(
                'productionsAndExports.createNewProduction.productionComponents.showOptionalInstructions'
              )}
        </span>

        <span
          className={`expandable-icon ${isOpen ? 'open' : ''}`}
          data-testid={`optional-instruction-span-text`}
        >
          <ExpandMoreIcon />
        </span>
      </button>
      {isOpen && (
        <div
          className="expandable-content"
          data-testid={`optional-instruction-div-btn`}
        >
          <ArrAccordionWithCheckbox
            sections={sectionsWithContent}
            onCheckChange={handleCheckChange}
            readAccordionTextIcon={true}
            disableCheckboxEdit={
              isInProgressOrCompleted || mode === FormMode.VIEW
            }
            allowMultiple={true}
          />
        </div>
      )}
    </div>
  );
};

export default OptionalInstruction;
